import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { Scale, Package, ShieldCheck, AlertTriangle, RefreshCw, Coins, Ghost } from "lucide-react";

const Terms = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = "Terms of service · Phantom Industries";
  }, []);

  return (
    <main className="min-h-screen bg-black text-white pt-24" data-testid="terms-page">
      <section className="relative">
        <div className="absolute inset-0 aurora-purple opacity-60 pointer-events-none" />
        <div className="relative max-w-4xl mx-auto px-6 py-14">
          <div className="chip"><Ghost className="h-3 w-3" /> Terms of service</div>
          <h1 className="mt-4 font-headline text-5xl md:text-6xl font-bold leading-[0.95] text-gradient-purple">
            The deal in plain English.
          </h1>
          <p className="mt-4 text-white/60 max-w-2xl">
            By placing an order with Phantom Industries, you agree to these terms.
            They are short on purpose.
          </p>
          <div className="mt-4 text-xs text-white/40">
            Last updated: {new Date().getFullYear()}
          </div>
        </div>
      </section>

      <section className="pb-24">
        <div className="max-w-4xl mx-auto px-6 space-y-6">
          <Card icon={Scale} title="1. Who you're buying from.">
            Phantom Industries is an Australian-registered small business operating out
            of Melbourne, VIC. All sales are subject to Australian Consumer Law.
            Your statutory rights are not affected by anything in these terms.
          </Card>

          <Card icon={Package} title="2. What we sell.">
            Genuine Google Pixel hardware, purchased new from authorised
            channels, and re-installed with our chosen hardened GrapheneOS
            profile at the tier you selected. The hardware is a Pixel; the
            software configuration and support is Phantom Industries.
          </Card>

          <Card icon={Coins} title="3. Payment.">
            <ul className="mt-2 space-y-2 list-disc list-inside marker:text-purple-400">
              <li>
                Crypto payments (XMR, LTC) qualify for our advertised discount
                and are quoted at a live exchange rate that we lock at the
                moment your order is placed.
              </li>
              <li>
                Bank transfer orders are billed at the rate shown on the site at
                order time.
              </li>
              <li>
                Orders are queued for build once payment is verified on-chain
                (crypto) or received in our account (bank).
              </li>
            </ul>
          </Card>

          <Card icon={ShieldCheck} title="4. Warranty.">
            <ul className="mt-2 space-y-2 list-disc list-inside marker:text-purple-400">
              <li>
                <span className="text-white/90">Hardware</span> — covered by
                Google's factory warranty via your receipt (typically 12
                months).
              </li>
              <li>
                <span className="text-white/90">Our workmanship</span> —
                Phantom Industries guarantees the software installation and hardening
                configuration for 12 months from delivery. If our build has an
                issue, we re-flash or refund.
              </li>
              <li>
                Physical damage, water damage, and rooting by the customer are
                not covered.
              </li>
            </ul>
          </Card>

          <Card icon={RefreshCw} title="5. Returns.">
            You have 14 days from delivery to return an unopened, factory-sealed
            device for a full refund. Once a device is unboxed, its
            tamper-evident seal is broken and we can no longer resell it —
            returns after that point are at our discretion and may be subject to
            a restocking fee.
          </Card>

          <Card icon={AlertTriangle} title="6. What you agree to do with the device.">
            You take responsibility for using the device lawfully in your
            jurisdiction. Encryption is legal in Australia and in most of the
            world; you are responsible for checking your local laws if you
            travel with the device. Phantom Industries is not liable for how you
            choose to use the phone.
          </Card>

          <Card icon={ShieldCheck} title="7. Limitation of liability.">
            To the maximum extent permitted by law, our liability is limited to
            replacement of the device or refund of the amount you paid us.
            Nothing in these terms limits liability for death, personal injury,
            fraud, or anything else that cannot lawfully be excluded under
            Australian Consumer Law.
          </Card>

          <div className="mt-10 text-sm text-white/50 text-center">
            Questions? <Link to="/contact" className="text-purple-300 hover:text-white underline underline-offset-4">Contact us</Link>.
          </div>
        </div>
      </section>
    </main>
  );
};

const Card = ({ icon: Icon, title, children }) => (
  <div className="card-glass rounded-2xl p-6">
    <div className="flex items-center gap-3">
      <div className="h-9 w-9 rounded-lg bg-purple-500/10 border border-purple-500/30 flex items-center justify-center">
        <Icon className="h-4 w-4 text-purple-300" />
      </div>
      <div className="font-display font-semibold text-white">{title}</div>
    </div>
    <div className="mt-3 text-sm text-white/70 leading-relaxed">{children}</div>
  </div>
);

export default Terms;
